/**
 * Engraving upcharge orphan cleanup.
 *
 * The engraving widget bundles a hidden "Gravur-Aufpreis" line item with the
 * main product via a shared `_bundle_id` property. Shopify does not auto-link
 * these — so if the customer removes the main item, the upcharge would linger
 * in the cart as an orphan. This script runs on every cart update and on
 * initial page load, scans for upcharge lines whose paired main item is gone,
 * and quietly removes them via /cart/change.js.
 *
 * Loaded globally via snippets/scripts.liquid so it works on PDPs, /cart, and
 * the cart drawer.
 */
(() => {
  const UPCHARGE_HANDLE = 'gravur-aufpreis';
  let inFlight = false;

  async function cleanupOrphans() {
    if (inFlight) return;
    inFlight = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

      // Collect bundle_ids that still have a "main" line item present
      const livingBundles = new Set();
      for (const item of cart.items) {
        if (!item.properties) continue;
        const bundleId = item.properties._bundle_id;
        const role = item.properties._bundle_role;
        if (bundleId && role !== 'upcharge') {
          livingBundles.add(bundleId);
        }
      }

      // Find upcharge orphans
      const orphans = cart.items.filter((item) => {
        if (item.handle !== UPCHARGE_HANDLE) return false;
        const bundleId = item.properties?._bundle_id;
        return !bundleId || !livingBundles.has(bundleId);
      });

      if (orphans.length === 0) return;

      // Remove orphans by line index. Shopify line index is 1-based for /cart/change.js
      // We remove from the back to keep earlier indices stable across calls.
      const sortedByIndexDesc = orphans
        .map((item) => ({ key: item.key, lineFromEnd: cart.items.indexOf(item) + 1 }))
        .sort((a, b) => b.lineFromEnd - a.lineFromEnd);

      for (const orphan of sortedByIndexDesc) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: orphan.key, quantity: 0 }),
        });
      }

      // Notify the theme so cart UI re-renders
      const refreshed = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: refreshed,
            sourceId: 'engraving-cleanup',
            data: { source: 'engraving-cleanup' },
          },
        })
      );
    } catch (err) {
      console.warn('[engraving-cleanup] failed', err);
    } finally {
      inFlight = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupOrphans, { once: true });
  } else {
    cleanupOrphans();
  }

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'engraving-cleanup') return;
    cleanupOrphans();
  });
})();
